import { DataResult, State } from "@progress/kendo-data-query";
import * as React from "react";
import { useState } from "react";
import {
  getSelectedState,
  GridDataStateChangeEvent,
  GridRowDoubleClickEvent,
  GridSelectionChangeEvent,
  GridToolbar,
  GridColumn,
} from "@progress/kendo-react-grid";
import { useNavigate } from "react-router-dom";
import { DatePickerPropsContext } from "@progress/kendo-react-dateinputs";
import { NumericTextBoxPropsContext } from "@progress/kendo-react-inputs";
import { useTheme } from "@mui/material";
import StyledKendoGrid from "../../../Grid/StyledKendoGrid";
import { GridDataLoader } from "../../../Grid/GridDataLoader";
import ProjectOverviewGridColumns from "./ProjectOverviewGridColumns";
import { DATA_ITEM_KEY, idGetter, SELECTED_FIELD } from "./ProjectOverviewGridConstans";
import PersonalFilter from "../../../PersonalFilter/PersonalFilter";
import ExportRasButton from "./ExportRasButton";
import { createProject } from "./CreateProject";
import { InitialState } from "../../../Grid/InitialDataState";
import { OnRowClickType } from "../../../Layout/Grid/GridRowClickProps";
import { projectUrls } from "../../../URL/ApplicationUrls";
import PageHeader from "../../../Layout/Header/PageHeader";
import CreateButton from "../../../TransportNetwork/PlantDockSupplierRelations/PdsrValidationItemGrid/Controls/CreateButton";
import { useDefaultDatePickerProps } from "../../../Grid/useDefaultDatePickerProps";
import { useDefaultNumericTextBoxProps } from "../../../Grid/useDefaultNumericTextBoxProps";
import StatePersistenceButtons from "../../../Grid/StatePersistenceButtons";
import useSavedState from "../../../Grid/UseSavedState";
import { isColumnFilterActive } from "../../../Grid/Extensions/FilterStatusHelper";
import { gridPagerDefaultSettings } from "../../../Grid/GridConstants";
import { combinedUrls } from "../../../URL/functions";
import RenderAuthorized from "../../../Auth/RenderAuthorized";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

const PROJECTOVERVIEWGRID_DATASTATE = "ProjectOverviewGrid_DataState";

export default function ProjectOverviewGrid() {
  const navigate = useNavigate();

  const initialDataState: State = {
    ...InitialState,
    sort: [{ field: "ProjectId", dir: "desc" }],
  };

  const [dataState, setDataState] = useSavedState<State>(
    PROJECTOVERVIEWGRID_DATASTATE,
    initialDataState
  );
  const [dataResult, setDataResult] = React.useState<DataResult>({
    data: [],
    total: 0,
  });

  const [selectedState, setSelectedState] = React.useState<{
    [id: number]: boolean | number[];
  }>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      let newSelectedState = getSelectedState({
        event,
        selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });

      newSelectedState = toggleCheckBox(newSelectedState);

      const selectedIds = Object.keys(newSelectedState)
        .map((id) => {
          return newSelectedState[id] === true ? id : null;
        })
        .filter((val) => val != null)
        .map((x) => Number(x));

      setSelectedIds(selectedIds);

      function toggleCheckBox(newSelectedState: { [p: string]: boolean | number[] }) {
        const sameID = Object.keys(newSelectedState)[0];
        if (sameID === Object.keys(selectedState)[0]) {
          newSelectedState[sameID] = !selectedState[sameID as never];
        }
        setSelectedState(newSelectedState);
        return newSelectedState;
      }
    },
    [selectedState]
  );

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const onDataReceived = (newDataResult: DataResult) => {
    setDataResult(newDataResult);
  };

  const handleRowDoubleClick = (e: GridRowDoubleClickEvent) => {
    navigate(projectUrls.projectWithId(e.dataItem[DATA_ITEM_KEY]).uiPath);
  };

  const onCreateProjectClick = async () => {
    const newProjectId = await createProject();
    if (!newProjectId) {
      throw new Error("Error: Create Project failed and ProjectId is undefinded");
    }
    navigate(projectUrls.projectWithId(newProjectId).uiPath);
  };

  function removeSelection(id: number): void {
    setSelectedState({});
    setSelectedIds([]);
  }

  function addSelection(id: number): void {
    setSelectedState({ [id]: true });
    setSelectedIds([...selectedIds, id]);
  }

  const onRowClick: OnRowClickType = (dataItem) => {
    const id = dataItem[DATA_ITEM_KEY];
    const isAlreadySelected = Object.keys(selectedState).find((x) => Number(x) === id);
    if (isAlreadySelected) {
      removeSelection(id);
      return;
    }

    addSelection(id);
  };

  const datePickerProps = useDefaultDatePickerProps();
  const numericTextBoxProps = useDefaultNumericTextBoxProps();
  const theme = useTheme();

  const columns = ProjectOverviewGridColumns({ onRowClick });

  return (
    <div>
      <PageHeader headerText="Project" />
      <PersonalFilter
        dataState={dataState}
        gridName="ProjectOverview"
        onApplyState={setDataState}
      />
      <DatePickerPropsContext.Provider value={datePickerProps}>
        <NumericTextBoxPropsContext.Provider value={numericTextBoxProps}>
          <StyledKendoGrid
            muiTheme={theme}
            height="70vh"
            data={{
              data: dataResult.data.map((item) => ({
                ...item,
                [SELECTED_FIELD]: selectedState[idGetter(item)],
              })),
              total: dataResult.total,
            }}
            sortable
            filterable
            pageable={{
              ...gridPagerDefaultSettings,
            }}
            resizable
            {...dataState}
            onDataStateChange={onDataStateChange}
            onRowDoubleClick={handleRowDoubleClick}
            dataItemKey={DATA_ITEM_KEY}
            selectedField={SELECTED_FIELD}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "single",
            }}
            onSelectionChange={onSelectionChange}
          >
            <GridToolbar>
              <StatePersistenceButtons
                localStorageKey={PROJECTOVERVIEWGRID_DATASTATE}
                initialState={initialDataState}
                dataState={dataState}
                resetButtonText="RESET FILTER"
                saveButtonText="SAVE FILTER"
                align="left"
              />
              <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
                <CreateButton onClick={onCreateProjectClick} />
              </RenderAuthorized>

              <RenderAuthorized
                requiredRoles={AuthorizationPolicies.RequireAdminReadOnlyNetworkRiskLaunch}
              >
                <ExportRasButton selectedProjectId={selectedIds[0]} />
              </RenderAuthorized>
            </GridToolbar>
            {columns.map((column) => (
              <GridColumn
                key={column.key}
                {...column.props}
                headerClassName={
                  isColumnFilterActive(column.props.field, dataState) ? "filterActive" : ""
                }
              />
            ))}
          </StyledKendoGrid>
          <GridDataLoader
            dataState={dataState}
            onDataReceived={onDataReceived}
            url={combinedUrls(projectUrls.base.apiUrl, "KendoGrid")}
          />
        </NumericTextBoxPropsContext.Provider>
      </DatePickerPropsContext.Provider>
    </div>
  );
}
